import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useVariantExplorerExtended } from "./hooks/useVariantExplorerExtended";
import VariantTableExtended from "./components/VariantTableExtended";
import CoverageBar from "./components/CoverageBar";
import {
  SORT_OPTIONS,
  VARIANT_MODES,
  VARIANT_MODE_OPTIONS,
  REPETITION_DISPLAY,
  REPETITION_DISPLAY_OPTIONS,
} from "./constants";
import BaseTile from "../../../components/Tile/BaseTile";
import TileHeader, {
  TileHeaderControls,
  TileHeaderSelect,
  TileHeaderButton,
} from "../../../components/Tile/TileHeader";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { cn } from "../../../utils/cn";
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { makeRequest } from "../../../services/apiClient";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import DateRangePresets from "../../filter/TabFilterModal/DateRangePresets";
import DateRangeFilterControl from "../../../components/common/FilterControls/DateRangeFilterControl";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import { Settings } from "lucide-react";
import { generateActivityColorPalette } from "./utils/colorUtils";
import { getUniqueActivityCodes, filterCatalogByVariants } from "./utils/activityUtils";

// Modern Pagination Component (reused from original)
const Pagination = ({
  currentPage,
  totalPages,
  setCurrentPage,
  getPageGroup,
}) => {
  const pageGroup = getPageGroup();
  const minPageInGroup = pageGroup[0];
  const maxPageInGroup = pageGroup[pageGroup.length - 1];

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center mt-6 mb-4">
      {/* Pagination Controls */}
      <div className="flex items-center space-x-1">
        {/* First Page */}
        {currentPage > 2 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
              title="First page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
            {minPageInGroup > 2 && (
              <span className="px-2 py-2 text-gray-400">...</span>
            )}
          </>
        )}

        {/* Previous Page */}
        {currentPage > 1 && (
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            title="Previous page"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Page Numbers */}
        {pageGroup.map((num) => (
          <button
            key={num}
            onClick={() => setCurrentPage(num)}
            className={cn(
              "inline-flex items-center px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-md min-w-[2.5rem] justify-center",
              num === currentPage
                ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
            )}
          >
            {num}
          </button>
        ))}

        {/* Next Page */}
        {currentPage < totalPages && (
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            title="Next page"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}

        {/* Last Page */}
        {currentPage < totalPages - 1 && (
          <>
            {maxPageInGroup < totalPages - 1 && (
              <span className="px-2 py-2 text-gray-400">...</span>
            )}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
              title="Last page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const VariantExplorerExtended = ({ activeFile, onRefresh, onGetHeaderActions, onRegisterSettingsGetter, initialSettings, onTitleChange }) => {
  const api = useGlobalAPI(activeFile);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [initError, setInitError] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [sliderPercent, setSliderPercent] = useState(100);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [matchingRule, setMatchingRule] = useState("");
  const [filterMode, setFilterMode] = useState("include");
  const [isActivityFilterActive, setIsActivityFilterActive] = useState(false);
  const [preActivityFilterSelection, setPreActivityFilterSelection] = useState([]);
  const [appliedActivityFilter, setAppliedActivityFilter] = useState(null);
  const [variantMode, setVariantMode] = useState(VARIANT_MODES.EXACT);
  const [repetitionDisplay, setRepetitionDisplay] = useState(REPETITION_DISPLAY.EXACT);
  const [componentTitle, setComponentTitle] = useState("Variant Explorer Extended");
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.selectedObjectType !== undefined) setSelectedObjectType(initialSettings.selectedObjectType);
    if (initialSettings.sliderPercent !== undefined) setSliderPercent(initialSettings.sliderPercent);
    if (initialSettings.selectedActivities !== undefined) setSelectedActivities(initialSettings.selectedActivities);
    if (initialSettings.matchingRule !== undefined) setMatchingRule(initialSettings.matchingRule);
    if (initialSettings.filterMode !== undefined) setFilterMode(initialSettings.filterMode);
    if (initialSettings.isActivityFilterActive !== undefined) setIsActivityFilterActive(initialSettings.isActivityFilterActive);
    if (initialSettings.preActivityFilterSelection !== undefined) setPreActivityFilterSelection(initialSettings.preActivityFilterSelection);
    if (initialSettings.variantMode !== undefined) setVariantMode(initialSettings.variantMode);
    if (initialSettings.repetitionDisplay !== undefined) setRepetitionDisplay(initialSettings.repetitionDisplay);
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.dateRangeOverride !== undefined) setDateRangeOverride(initialSettings.dateRangeOverride);
  }, []);

  const {
    loading,
    isFiltered,
    isVariantFilterActive,
    error,
    hasLoaded,
    variants,
    summary,
    activityCatalog,
    selectedVariants,
    currentVariants,
    totalPages,
    currentPage,
    sortKey,
    sortOrder,
    sortOption,
    handleToggleSelect,
    toggleSelectAll,
    handleBulkSelection,
    handleSortChange,
    handleColumnSort,
    handleFilter,
    handleReset,
    setCurrentPage,
    getPageGroup,
    refetchData,
    dateRangeOverride,
    setDateRangeOverride,
  } = useVariantExplorerExtended(selectedObjectType, activeFile, variantMode, repetitionDisplay, initialSettings?.sortOption, appliedActivityFilter);

  // Whether the activity filter needs to be re-applied to the fetched data once
  // the initial (unfiltered) load completes. Only relevant on mount, so a ref.
  const pendingActivityFilterReapply = useRef(
    !!(initialSettings?.isActivityFilterActive && initialSettings?.selectedActivities?.length > 0)
  );

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      selectedObjectType,
      sliderPercent,
      selectedActivities,
      matchingRule,
      filterMode,
      isActivityFilterActive,
      preActivityFilterSelection,
      variantMode,
      repetitionDisplay,
      componentTitle,
      sortOption,
      dateRangeOverride,
    }));
  }, [selectedObjectType, sliderPercent, selectedActivities, matchingRule, filterMode, isActivityFilterActive, preActivityFilterSelection, variantMode, repetitionDisplay, componentTitle, sortOption, dateRangeOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-sync the tile title to the parent (GridComponent header)
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle);
  }, [componentTitle]);

  // Generate color palette for activities
  const colorPalette = useMemo(() => {
    const activityCodes = getUniqueActivityCodes(variants);
    return generateActivityColorPalette(activityCodes);
  }, [variants]);

  // Build activity catalog from variants and backend data
  const fullActivityCatalog = useMemo(() => {
    // If backend provides catalog, use it; otherwise build from variants
    if (activityCatalog && Object.keys(activityCatalog).length > 0) {
      // Backend format: { activity_codes: { "Full Name": "CODE" }, all_activities: [...] }
      const catalog = {};
      
      // Check if activity_codes exists - backend gives us { "Full Name": "CODE" }
      if (activityCatalog.activity_codes && typeof activityCatalog.activity_codes === 'object') {
        // Backend gives us { "Full Name": "CODE" }, we need { "CODE": { code: "CODE", name: "Full Name" } }
        Object.entries(activityCatalog.activity_codes).forEach(([fullName, code]) => {
          if (code && fullName) {
            catalog[code] = { code, name: fullName };
          }
        });
      }
      
      console.log('📚 Activity catalog from backend:', catalog, 'Keys:', Object.keys(catalog));
      return catalog;
    }
    
    // Build from variant data
    const catalog = {};
    variants.forEach(variant => {
      if (variant.activity_sequence && Array.isArray(variant.activity_sequence)) {
        variant.activity_sequence.forEach(activity => {
          const code = activity.activity_code || activity.code;
          const name = activity.activity || activity.activity_name || activity.name || code;
          if (code && !catalog[code]) {
            catalog[code] = { code, name };
          }
        });
      }
    });
    console.log('📚 Activity catalog built from variants:', catalog, 'Total variants:', variants.length, 'Keys:', Object.keys(catalog));
    return catalog;
  }, [activityCatalog, variants]);

  // Filter activity catalog to only show activities present in current variants
  const filteredActivityCatalog = useMemo(() => {
    const filtered = filterCatalogByVariants(fullActivityCatalog, variants);
    console.log('🔍 Filtered activity catalog:', filtered, 'Keys:', Object.keys(filtered), 'From full catalog keys:', Object.keys(fullActivityCatalog));
    
    // Fallback: if filtered is empty but we have variants, use fullActivityCatalog
    if (Object.keys(filtered).length === 0 && variants.length > 0) {
      console.warn('⚠️ Filtered catalog is empty, falling back to full catalog');
      return fullActivityCatalog;
    }
    
    return filtered;
  }, [fullActivityCatalog, variants]);

  // Extract unique activities from all variants for activity filter dropdown.
  const uniqueActivities = useMemo(() => {
    const activities = new Set(selectedActivities);
    (appliedActivityFilter?.activities || []).forEach(activity => activities.add(activity));
    variants.forEach(variant => {
      if (variant.activities && Array.isArray(variant.activities)) {
        variant.activities.forEach(activity => {
          if (activity && typeof activity === 'string') {
            activities.add(activity);
          }
        });
      }
    });
    return Array.from(activities).sort();
  }, [variants, selectedActivities, appliedActivityFilter]);


  // Reload when global filters change (if already initialized)
  useEffect(() => {
    if (isInitialized && activeFile) {
      console.log(
        "🔄 VariantExplorerExtended: Global filters changed, reloading metadata..."
      );
      handleInitialize();
    }
  }, [api.currentFilters, activeFile, isInitialized]);

  // Reload variant data when global filters change but object type selection is stable
  useEffect(() => {
    if (isInitialized && selectedObjectType && activeFile && refetchData) {
      console.log(
        "🔄 VariantExplorerExtended: Global filters changed, refetching variant data..."
      );
      refetchData();
    }
  }, [api.currentFilters]);

  // Reload variant data when this tile's own local date range override changes
  useEffect(() => {
    if (isInitialized && selectedObjectType && activeFile && refetchData) {
      refetchData();
    }
  }, [dateRangeOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply the activity filter to the fetched data once the initial load
  // completes, if it was active when the config was last saved. This also
  // commits the appliedActivityFilter snapshot (same as a real Apply click),
  // so subsequent automatic refetches (mode change, etc.) keep reusing it.
  useEffect(() => {
    if (!hasLoaded || !pendingActivityFilterReapply.current) return;
    pendingActivityFilterReapply.current = false;
    const filterObj = buildActivityFilterObj();
    setAppliedActivityFilter(filterObj);
    refetchData(filterObj);
    setIsActivityFilterActive(true);
  }, [hasLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selected object type if it's no longer in the available list
  useEffect(() => {
    if (
      objectTypes.length > 0 &&
      selectedObjectType &&
      !objectTypes.includes(selectedObjectType)
    ) {
      console.log(
        `🔄 VariantExplorerExtended: Current selection '${selectedObjectType}' not in filtered list. Resetting to '${objectTypes[0]}'`
      );
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  const handleInitialize = async () => {
    if (!activeFile) return;
    setInitLoading(true);
    setInitError(null);
    try {
      const params = { file_type: activeFile };
      const response = await makeRequest(
        "/object-types",
        { params },
        "Object Types"
      );

      const allTypes = Array.isArray(response.objectTypes)
        ? response.objectTypes
        : response;

      // Apply client-side filtering based on current global filters (excluding variant filter)
      const cleanFilters = { ...api.currentFilters };
      delete cleanFilters.variantFilter;
      const activeFilters = cleanFilters;
      let filteredTypes = allTypes;

      // Apply object type filter if specified
      if (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) {
        filteredTypes = allTypes.filter((type) =>
          activeFilters.objectTypes.includes(type)
        );
      }

      console.log("📊 VariantExplorerExtended metadata loaded:", {
        allTypes: allTypes.length,
        filteredTypes: filteredTypes.length,
        hasFilters:
          activeFilters.objectTypes && activeFilters.objectTypes.length > 0,
      });

      if (!filteredTypes || filteredTypes.length === 0) {
        throw new Error("No object types were found for the current filters.");
      }

      setObjectTypes(filteredTypes);

      // If the currently selected object type is no longer available, select the first available type
      // Always update if current selection is not valid for filtered types
      if (!selectedObjectType || !filteredTypes.includes(selectedObjectType)) {
        const newObjectType = filteredTypes[0];

        console.log(
          `🔄 VariantExplorerExtended: Setting object type from '${selectedObjectType}' to '${newObjectType}'` +
          ' (first available)'
        );
        setSelectedObjectType(newObjectType); // This triggers the data fetch in the hook
      }

      setIsInitialized(true);
    } catch (err) {
      console.error("Failed to initialize Variant Explorer Extended:", err);
      setInitError("Failed to load initial configuration. Please try again.");
    } finally {
      setInitLoading(false);
    }
  };

  // Apply initial slider selection after variants are loaded
  useEffect(() => {
    if (variants.length > 0) {
      const sortedVariants = [...variants].sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];
        if (sortKey === "variant_id") {
          return sortOrder === "asc"
            ? a.variant_id.localeCompare(b.variant_id)
            : b.variant_id.localeCompare(a.variant_id);
        }
        if (sortKey === "abc_xyz_class") {
          // Composite class is a 2-char string or null; sort alphabetically and
          // keep nulls at the bottom (mirrors the table's display sort).
          const aCls = a.abc_xyz_class;
          const bCls = b.abc_xyz_class;
          if (!aCls && !bCls) return 0;
          if (!aCls) return 1;
          if (!bCls) return -1;
          return sortOrder === "asc"
            ? aCls.localeCompare(bCls)
            : bCls.localeCompare(aCls);
        }
        if (sortKey === "median_lead_time") {
          aVal = a.lead_time_stats?.median ?? 0;
          bVal = b.lead_time_stats?.median ?? 0;
        }
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      });

      const variantsToSelect = Math.max(
        1,
        Math.floor((sliderPercent / 100) * sortedVariants.length)
      );
      const selectedFromSlider = sortedVariants.slice(0, variantsToSelect);
      const expectedSelectedIds = selectedFromSlider.map((v) => v.variant_id);

      const currentSelectionSet = new Set(selectedVariants);
      const expectedSelectionSet = new Set(expectedSelectedIds);

      const selectionChanged =
        currentSelectionSet.size !== expectedSelectionSet.size ||
        [...expectedSelectionSet].some((id) => !currentSelectionSet.has(id));

      if (selectionChanged) {
        console.log("🔄 Applying slider selection:", {
          percentage: sliderPercent,
          variantsToSelect,
          selectedIds: expectedSelectedIds,
        });
        handleSliderSelection(expectedSelectedIds);
      }
    }
  }, [variants, sliderPercent]);

  const totalCases = summary?.total_objects || 0;

  const selectedCases = useMemo(() => {
    return variants
      .filter((v) => selectedVariants.includes(v.variant_id))
      .reduce((sum, v) => sum + v.trace_count, 0);
  }, [variants, selectedVariants]);

  const percentCovered = useMemo(() => {
    return totalCases > 0 ? (selectedCases / totalCases) * 100 : 0;
  }, [selectedCases, totalCases]);

  const hasData = isInitialized || variants.length > 0;

  // Auto-load data when persistent cache is enabled
  useAutoLoad(handleInitialize, [activeFile], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(handleInitialize);
    }
  }, [onRefresh, handleInitialize]);

  // Provide header actions (settings gear) to parent (GridComponent).
  // The parent recreates onGetHeaderActions on every render and stores the
  // element in state, so depending on the callback identity loops forever —
  // keep it in a ref (same pattern as GanttChart) and register once on mount.
  const onGetHeaderActionsRef = React.useRef(onGetHeaderActions);
  React.useEffect(() => {
    onGetHeaderActionsRef.current = onGetHeaderActions;
  });
  React.useEffect(() => {
    onGetHeaderActionsRef.current?.(
      <button
        onClick={() => setIsSettingsModalOpen(true)}
        title="Settings"
        className="rounded p-1 hover:bg-muted transition-colors"
      >
        <Settings className="h-4 w-4" />
      </button>,
    );
  }, []);

  const unselectedVariants = variants.filter(
    (v) => !selectedVariants.includes(v.variant_id)
  );

  const othersCases = unselectedVariants.reduce(
    (sum, v) => sum + v.trace_count,
    0
  );

  const othersPercentage =
    totalCases > 0 ? (othersCases / totalCases) * 100 : 0;

  const weightedLeadTimes = [];
  unselectedVariants.forEach((v) => {
    const median = parseFloat(v.lead_time_stats?.median);
    const count = v.trace_count;
    if (!isNaN(median) && count > 0) {
      weightedLeadTimes.push(...Array(count).fill(median));
    }
  });

  const noUnselected = unselectedVariants.length === 0;
  const medianLeadTime =
    weightedLeadTimes.length > 0
      ? weightedLeadTimes.sort((a, b) => a - b)[
          Math.floor(weightedLeadTimes.length / 2)
        ]
      : null;

  const others = {
    object_count: noUnselected ? "–" : othersCases,
    percentage: othersPercentage,
    num_steps: noUnselected ? "–" : "-",
    median_lead_time: noUnselected ? "–" : medianLeadTime ?? "–",
  };

  const isOverallLoading = initLoading || loading;
  const showVariantContent = isInitialized && hasLoaded && !loading && !error;
  const hasVariants = variants.length > 0;

  const handleFullReset = useCallback(() => {
    handleReset();
    setSliderPercent(100);
  }, [handleReset]);

  const handleSliderSelection = useCallback(
    (variantIds) => {
      handleBulkSelection(variantIds);
    },
    [handleBulkSelection]
  );

  const handleActivitySelection = (activities) => {
    setSelectedActivities(activities);

    if (activities.length === 0) {
      setFilterMode("include");
    }
  };

  const buildActivityFilterObj = () => ({
    enabled: true,
    activities: selectedActivities,
    matchType: matchingRule === "any" ? "any" : "all",
    mode: filterMode,
  });

  // Apply activity filter
  const handleActivityFilter = async () => {
    if (selectedActivities.length === 0) return;

    // Save current selection state before applying activity filter
    if (!isActivityFilterActive) {
      setPreActivityFilterSelection([...selectedVariants]);
    }

    const filterObj = buildActivityFilterObj();
    setAppliedActivityFilter(filterObj);
    await refetchData(filterObj);

    setIsActivityFilterActive(true);
    console.log(`✅ Activity filter applied with matchType="${matchingRule}", mode="${filterMode}" on activities:`, selectedActivities);
  };

  // Reset activity filter independently without affecting main filter
  const handleActivityReset = async () => {
    console.log('🔄 Resetting activity filter...');
    
    if (isActivityFilterActive && preActivityFilterSelection.length > 0) {
      handleBulkSelection(preActivityFilterSelection);
      console.log(`✅ Restored previous selection: ${preActivityFilterSelection.length} variants`);
    } else {
      handleBulkSelection(variants.map((v) => v.variant_id));
      console.log(`✅ Selecting all variants: ${variants.length} total`);
    }
    setAppliedActivityFilter(null);
    await refetchData(null);

    setIsActivityFilterActive(false);
    setSelectedActivities([]);
    setMatchingRule("any");
    setFilterMode("include");
    setPreActivityFilterSelection([]);
    
    console.log('✅ Activity filter cleared, showing all variants');
  };

  return (
    <>
    <BaseTile
      loading={isOverallLoading}
      error={initError || error}
      minHeight="xl"
      className="h-full mb-6"
    >
      {!isInitialized ? (
        <TileContent>
          <TileStatusContent
            status="info"
            title="Load Extended Variant Data"
            message="Click the button to load the extended variant explorer with activity sequences."
            action={
              <TileHeaderButton
                variant="primary"
                size="sm"
                onClick={handleInitialize}
                disabled={initLoading}
              >
                {initLoading ? "Loading..." : "Load Data"}
              </TileHeaderButton>
            }
          />
        </TileContent>
      ) : (
        <>
          {!isOverallLoading && (
            <TileHeader
              layout="between"
              size="default"
              controls={
                <TileHeaderControls className="flex flex-wrap gap-4 items-end">
                  <div className="flex flex-col gap-1 min-w-[180px]">
                    <label className="text-xs font-medium text-gray-700">
                      Tile Title
                    </label>
                    <input
                      type="text"
                      value={componentTitle}
                      onChange={(e) => setComponentTitle(e.target.value)}
                      placeholder="Variant Explorer Extended"
                      disabled={loading}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <TileHeaderSelect
                    label="Object Type"
                    value={selectedObjectType}
                    onChange={(e) => setSelectedObjectType(e.target.value)}
                    disabled={loading}
                    options={objectTypes.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                  />
                  <TileHeaderSelect
                    label="Variant Mode"
                    value={variantMode}
                    onChange={(e) => setVariantMode(e.target.value)}
                    disabled={loading}
                    options={VARIANT_MODE_OPTIONS}
                  />
                  {variantMode === VARIANT_MODES.LOOP_COMPRESSED && (
                    <TileHeaderSelect
                      label="Repetition Display"
                      value={repetitionDisplay}
                      onChange={(e) => setRepetitionDisplay(e.target.value)}
                      disabled={loading}
                      options={REPETITION_DISPLAY_OPTIONS}
                      title="Show concrete loop multipliers (Exact) or replace them with an abstract N (Abstract)."
                    />
                  )}
                  <TileHeaderSelect
                    label="Sort"
                    value={sortOption}
                    onChange={(e) => handleSortChange(e.target.value)}
                    disabled={loading}
                    options={SORT_OPTIONS.map((opt) => ({
                      value: opt,
                      label: opt,
                    }))}
                  />
                  <div className="flex flex-col gap-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-700">
                      Variants Coverage ({sliderPercent}%)
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={sliderPercent}
                      onChange={(e) => {
                        setSliderPercent(Number(e.target.value));
                        if (variants.length > 0) {
                          const sortedVariants = [...variants].sort((a, b) => {
                            let aVal = a[sortKey];
                            let bVal = b[sortKey];

                            if (sortKey === "variant_id") {
                              return sortOrder === "asc"
                                ? a.variant_id.localeCompare(b.variant_id)
                                : b.variant_id.localeCompare(a.variant_id);
                            }

                            if (sortKey === "abc_xyz_class") {
                              // 2-char class string or null; alphabetical, nulls last.
                              const aCls = a.abc_xyz_class;
                              const bCls = b.abc_xyz_class;
                              if (!aCls && !bCls) return 0;
                              if (!aCls) return 1;
                              if (!bCls) return -1;
                              return sortOrder === "asc"
                                ? aCls.localeCompare(bCls)
                                : bCls.localeCompare(aCls);
                            }

                            if (sortKey === "median_lead_time") {
                              aVal = a.lead_time_stats?.median ?? 0;
                              bVal = b.lead_time_stats?.median ?? 0;
                            }

                            aVal = aVal ?? 0;
                            bVal = bVal ?? 0;

                            return sortOrder === "asc"
                              ? aVal - bVal
                              : bVal - aVal;
                          });

                          const variantsToSelect = Math.max(
                            1,
                            Math.floor(
                              (Number(e.target.value) / 100) *
                                sortedVariants.length
                            )
                          );
                          const selectedFromSlider = sortedVariants.slice(
                            0,
                            variantsToSelect
                          );
                          const selectedIds = selectedFromSlider.map(
                            (v) => v.variant_id
                          );
                          handleSliderSelection(selectedIds);
                        }
                      }}
                      disabled={loading}
                      className="h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb:appearance-none slider-thumb:h-4 slider-thumb:w-4 slider-thumb:rounded-full slider-thumb:bg-primary-500 slider-thumb:cursor-pointer slider-thumb:shadow-sm hover:slider-thumb:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
                      style={{
                        background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${sliderPercent}%, #E5E7EB ${sliderPercent}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>
                </TileHeaderControls>
              }
              actions={
                <div className="flex gap-2">
                  <TileHeaderButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleFilter('fade')}
                    disabled={loading || selectedVariants.length === 0}
                  >
                    Filter Faded
                  </TileHeaderButton>
                  <TileHeaderButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleFilter('hide')}
                    disabled={loading || selectedVariants.length === 0}
                  >
                    Filter Excluded
                  </TileHeaderButton>
                  {summary && summary.total_variants && (
                    <TileHeaderButton
                      variant="secondary"
                      size="sm"
                      onClick={handleFullReset}
                      disabled={loading || !isVariantFilterActive}
                    >
                      Reset
                    </TileHeaderButton>
                  )}
                </div>
              }
            />
          )}

          <TileContent layout="flex" direction="column" grow>
            {isOverallLoading && (
              <TileStatusContent
                status="loading"
                title="Loading Extended Variants"
                message="Loading extended variant data with activity sequences..."
              />
            )}

            {showVariantContent && (
              <>
                {/* Activity Filter Controls */}
                <div className="border-b border-gray-200 p-4">
                  <div className="flex items-end justify-end gap-3 flex-nowrap">
                    <div className="flex-shrink-0 min-w-[280px]">
                      <MultiSelectDropdown
                        label="Activities"
                        labelPosition="inline"
                        options={uniqueActivities}
                        selectedValues={selectedActivities}
                        onSelectionChange={handleActivitySelection}
                        placeholder={uniqueActivities.length === 0 ? "Loading activities..." : "Select activities"}
                        disabled={loading || uniqueActivities.length === 0}
                        minOptionWidth="450px"
                        className="w-full"
                      />
                    </div>

                    <div className="flex-shrink-0 min-w-[200px]">
                      <TileHeaderSelect
                        label="Matching Rule"
                        value={matchingRule}
                        onChange={(e) => setMatchingRule(e.target.value)}
                        disabled={loading || selectedActivities.length === 0}
                        placeholder="Select matching rule..."
                        options={[
                          {
                            value: "any",
                            label: "Match any selected activity",
                          },
                          {
                            value: "all",
                            label: "Match all selected activities",
                          },
                        ]}
                        className="w-full"
                        title={selectedActivities.length === 0 ? "Please select at least one activity first" : ""}
                      />
                    </div>

                    {/* Include/exclude mode — reuses the Global Filter Activity
                        Node Filter values/labels. Backend applies the filtering. */}
                    <div className="flex-shrink-0 min-w-[160px]">
                      <TileHeaderSelect
                        label="Mode"
                        value={filterMode}
                        onChange={(e) => setFilterMode(e.target.value)}
                        disabled={loading || selectedActivities.length === 0}
                        placeholder="Select filter mode..."
                        options={[
                          { value: "include", label: "Include" },
                          { value: "exclude", label: "Exclude" },
                        ]}
                        className="w-full"
                        title={selectedActivities.length === 0 ? "Please select at least one activity first" : ""}
                      />
                    </div>

                    <div className="flex-shrink-0 flex gap-2 min-w-[300px]">
                      <TileHeaderButton
                        variant="primary"
                        size="sm"
                        onClick={handleActivityFilter}
                        disabled={
                          loading || selectedActivities.length === 0
                        }
                        title="Filter variants by selected activities"
                        className="whitespace-nowrap"
                      >
                        Apply Activity Filter
                      </TileHeaderButton>
                      <TileHeaderButton
                        variant="secondary"
                        size="sm"
                        onClick={handleActivityReset}
                        disabled={loading || !isActivityFilterActive}
                        title="Clear activity filter and show all variants"
                        className="whitespace-nowrap"
                      >
                        Reset Activities
                      </TileHeaderButton>
                    </div>
                  </div>
                </div>

                {/* Content Area */}
                {hasVariants ? (
                  <>
                    <CoverageBar
                      percentCovered={percentCovered}
                      selectedVariantsCount={selectedVariants.length}
                      totalVariants={summary?.total_variants}
                      selectedCases={selectedCases}
                      totalCases={summary?.total_objects}
                    />

                    <div className="flex-grow overflow-hidden">
                      <VariantTableExtended
                        variants={variants}
                        currentVariants={currentVariants}
                        selectedVariants={selectedVariants}
                        handleToggleSelect={handleToggleSelect}
                        toggleSelectAll={toggleSelectAll}
                        sortKey={sortKey}
                        sortOrder={sortOrder}
                        handleColumnSort={handleColumnSort}
                        loading={loading}
                        others={others}
                        colorPalette={colorPalette}
                        activityCatalog={filteredActivityCatalog}
                      />
                    </div>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                      getPageGroup={getPageGroup}
                    />
                  </>
                ) : (
                  <TileStatusContent
                    status="empty"
                    title="No Variants Available"
                    message={
                      isActivityFilterActive
                        ? `No variants match the selected ${matchingRule === "any" ? "any of the" : ""} activities.`
                        : "No variant data available for this object type."
                    }
                  />
                )}
              </>
            )}
          </TileContent>
        </>
      )}
    </BaseTile>
    <TileSettingsModal
      open={isSettingsModalOpen}
      onOpenChange={setIsSettingsModalOpen}
      title="Variant Explorer Extended – Einstellungen"
      getInitialValues={() => ({ dateRangeOverride })}
      onApply={(values) => setDateRangeOverride(values.dateRangeOverride)}
    >
      {({ values, setField }) => (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-gray-700">
            Date Range
          </label>
          <DateRangePresets
            value={values.dateRangeOverride}
            onChange={(v) => setField('dateRangeOverride', v)}
          />
          <DateRangeFilterControl
            value={values.dateRangeOverride}
            onChange={(v) => setField('dateRangeOverride', v)}
            activeFile={activeFile}
            disabled={loading}
          />
        </div>
      )}
    </TileSettingsModal>
    </>
  );
};

export default VariantExplorerExtended;
